import React, { useState } from 'react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { STATUSES, ASSET_ICONS, ASSET_LABELS } from '../utils';

// Covers the PATCH /assets/:id fields nothing else in the UI could reach.
//
// Location and assignment are deliberately absent even though the endpoint
// accepts them: TransferModal owns those and records transfer history, so
// setting them here would move an asset without leaving that trail.
//
// 'retired' is absent from the status list because retiring an asset also
// stamps deleted_at and retired_at. Picking it here would leave a "retired"
// asset still live in every list — that's the Retire action's job.
const EDITABLE_STATUSES = STATUSES.filter(s => s.value !== 'retired');

const FIELD_LABELS = {
  make: 'Make',
  model: 'Model',
  serialNumber: 'Serial Number',
  warrantyExpiry: 'Warranty Expiry',
  status: 'Status',
};

export default function EditAssetModal({ asset, onClose, onSuccess }) {
  const { withAuth } = useAuth();

  const initial = {
    make: asset.make || '',
    model: asset.model || '',
    serialNumber: asset.serial_number || '',
    status: asset.status || 'active',
    warrantyExpiry: asset.warranty_expiry ? asset.warranty_expiry.slice(0, 10) : '',
  };

  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function set(key, value) {
    setForm(f => ({ ...f, [key]: value }));
    setError('');
  }

  async function handleSubmit() {
    // Send only what changed. The endpoint COALESCEs every column, so an empty
    // string can't clear a value — it would be silently ignored and look like
    // the save failed. Refuse that up front instead.
    const payload = {};
    const blanked = [];

    for (const key of Object.keys(form)) {
      if (form[key] === initial[key]) continue;
      if (form[key] === '') { blanked.push(FIELD_LABELS[key]); continue; }
      payload[key] = form[key];
    }

    if (blanked.length) {
      setError(`The API can't clear a field once set, so ${blanked.join(' and ')} can only be changed to a new value, not emptied.`);
      return;
    }
    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }

    setSaving(true);
    setError('');
    try {
      await withAuth(() => api.updateAsset(asset.id, payload));
      onSuccess();
    } catch (err) {
      setError(err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  }

  // No click-outside-to-close: a stray backdrop click would discard typed work.
  return (
    <div className="modal-overlay">
      <div className="modal">
        <h3>Edit Asset Details</h3>

        <div style={{
          background: 'var(--bg-2)', borderRadius: 'var(--radius-sm)',
          padding: '12px 14px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ fontSize: 22 }}>{ASSET_ICONS[asset.asset_type]}</span>
          <div>
            <div style={{ fontWeight: 500, fontSize: 14 }}>{asset.display_name}</div>
            <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
              {asset.asset_tag} · {ASSET_LABELS[asset.asset_type]}
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="form-group">
            <label>Make</label>
            <input value={form.make} onChange={e => set('make', e.target.value)} placeholder="e.g. Apple" />
          </div>
          <div className="form-group">
            <label>Model</label>
            <input value={form.model} onChange={e => set('model', e.target.value)} placeholder="e.g. iPhone 15" />
          </div>
        </div>

        <div className="form-group">
          <label>Serial Number</label>
          <input
            value={form.serialNumber}
            onChange={e => set('serialNumber', e.target.value)}
            style={{ fontFamily: 'var(--mono)' }}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="form-group">
            <label>Status</label>
            <select value={form.status} onChange={e => set('status', e.target.value)}>
              {EDITABLE_STATUSES.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Warranty Expiry</label>
            <input type="date" value={form.warrantyExpiry} onChange={e => set('warrantyExpiry', e.target.value)} />
          </div>
        </div>

        <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 16 }}>
          Assignment and location are changed through Transfer, so the move is recorded in the asset's history.
        </div>

        {error && <div style={{ color: 'var(--red)', fontSize: 14, marginBottom: 12 }}>{error}</div>}

        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
